import React, { useState } from 'react';
import styled from 'styled-components';
import { User, Mail, Calendar, Shield, Key, Bell, Trash2, Save, Sun, Moon } from 'lucide-react';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input } from '../../components/UI/Input';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useForm } from '../../hooks/useForm';
import { validateEmail, validateUsername } from '../../utils/helpers';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

const ProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  }
  
  p {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin: 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const InfoCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 80px;
`;

const InfoValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const DangerZone = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.error}10;
`;

const DangerTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.error};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DangerDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const ThemeSettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ThemeOptions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ThemeOption = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ $selected, theme }) => 
    $selected ? theme.colors.primary : theme.colors.border
  };
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background-color: ${({ $selected, theme }) => 
    $selected ? theme.colors.primaryLight + '20' : theme.colors.surface
  };
  color: ${({ $selected, theme }) => 
    $selected ? theme.colors.primary : theme.colors.text
  };
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  flex: 1;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ $selected, theme }) => 
      $selected ? theme.colors.primaryLight + '20' : theme.colors.gray100
    };
  }
`;

const ThemeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const ThemeLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ThemeName = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ThemeDescription = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

function ThemeSetting() {
  const { isDarkMode, setThemeMode, isLoading } = useTheme();

  const handleThemeChange = (theme) => {
    setThemeMode(theme);
  };

  return (
    <ThemeSettingContainer>
      <ThemeOptions>
        <ThemeOption
          $selected={!isDarkMode}
          onClick={() => handleThemeChange('light')}
          disabled={isLoading}
        >
          <ThemeIcon>
            <Sun size={20} />
          </ThemeIcon>
          <ThemeLabel>
            <ThemeName>Light</ThemeName>
            <ThemeDescription>Clean and bright interface</ThemeDescription>
          </ThemeLabel>
        </ThemeOption>
        
        <ThemeOption
          $selected={isDarkMode}
          onClick={() => handleThemeChange('dark')}
          disabled={isLoading}
        >
          <ThemeIcon>
            <Moon size={20} />
          </ThemeIcon>
          <ThemeLabel>
            <ThemeName>Dark</ThemeName>
            <ThemeDescription>Easy on the eyes in low light</ThemeDescription>
          </ThemeLabel>
        </ThemeOption>
      </ThemeOptions>
    </ThemeSettingContainer>
  );
}

const validationRules = {
  email: [
    (value) => !value ? 'Email is required' : '',
    (value) => !validateEmail(value) ? 'Please enter a valid email' : '',
  ],
  username: [
    (value) => !value ? 'Username is required' : '',
    (value) => !validateUsername(value) ? 'Username must be 3-50 characters' : '',
  ],
  currentPassword: [
    (value) => !value ? 'Current password is required' : '',
  ],
  newPassword: [
    (value) => !value ? 'New password is required' : '',
    (value) => value.length < 8 ? 'Password must be at least 8 characters' : '',
  ],
  confirmPassword: [
    (value, allValues) => !value ? 'Please confirm your password' : '',
    (value, allValues) => value !== allValues.newPassword ? 'Passwords do not match' : '',
  ],
};

function ProfilePage() {
  const { user, updateUser, logout, loading } = useAuth();
  const { theme } = useTheme();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  const {
    values: profileValues,
    errors: profileErrors,
    handleChange: handleProfileChange,
    handleBlur: handleProfileBlur,
    validateForm: validateProfileForm,
  } = useForm({
    email: user?.email || '',
    username: user?.username || '',
  }, validationRules);

  const {
    values: passwordValues,
    errors: passwordErrors,
    handleChange: handlePasswordChange,
    handleBlur: handlePasswordBlur,
    validateForm: validatePasswordForm,
    reset: resetPasswordForm,
  } = useForm({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  }, validationRules);

  // Show loading state while user data is being fetched
  if (loading || !user) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Loading profile...
        </div>
      </ProfileContainer>
    );
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }

    try {
      // Here you would call an API to update the user profile
      // For now, we'll just update the local state
      updateUser(profileValues);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }

    try {
      // Here you would call an API to update the password
      toast.success('Password updated successfully');
      resetPasswordForm();
      setShowPasswordForm(false);
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted.')) {
      if (window.confirm('This is your final warning. Are you absolutely sure?')) {
        try {
          // Here you would call an API to delete the account
          toast.success('Account deleted successfully');
          logout();
        } catch (error) {
          toast.error('Failed to delete account');
        }
      }
    }
  };

  const getUserInitials = (username) => {
    return username
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <ProfileContainer>
      <Header>
        <Avatar>
          {getUserInitials(user?.username || 'U')}
        </Avatar>
        <UserInfo>
          <h1>{user?.username}</h1>
          <p>Member since {formatDate(user?.created_at)}</p>
        </UserInfo>
      </Header>

      <ContentGrid>
        <MainContent>
          <Section>
            <SectionTitle>
              <User size={20} />
              Profile Information
            </SectionTitle>
            <Form onSubmit={handleProfileSubmit}>
              <FormRow>
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={profileValues.email}
                  onChange={handleProfileChange}
                  onBlur={handleProfileBlur}
                  error={profileErrors.email}
                  required
                />
                <Input
                  label="Username"
                  name="username"
                  value={profileValues.username}
                  onChange={handleProfileChange}
                  onBlur={handleProfileBlur}
                  error={profileErrors.username}
                  required
                />
              </FormRow>
              <ButtonGroup>
                <Button type="submit" variant="primary">
                  <Save size={16} />
                  Save Changes
                </Button>
              </ButtonGroup>
            </Form>
          </Section>

          <Section>
            <SectionTitle>
              <Key size={20} />
              Security
            </SectionTitle>
            {!showPasswordForm ? (
              <Button 
                variant="secondary" 
                onClick={() => setShowPasswordForm(true)}
              >
                Change Password
              </Button>
            ) : (
              <Form onSubmit={handlePasswordSubmit}>
                <Input
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordValues.currentPassword}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  error={passwordErrors.currentPassword}
                  required
                />
                <FormRow>
                  <Input
                    label="New Password"
                    name="newPassword"
                    type="password"
                    value={passwordValues.newPassword}
                    onChange={handlePasswordChange}
                    onBlur={handlePasswordBlur}
                    error={passwordErrors.newPassword}
                    required
                  />
                  <Input
                    label="Confirm New Password"
                    name="confirmPassword"
                    type="password"
                    value={passwordValues.confirmPassword}
                    onChange={handlePasswordChange}
                    onBlur={handlePasswordBlur}
                    error={passwordErrors.confirmPassword}
                    required
                  />
                </FormRow>
                <ButtonGroup>
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={() => {
                      setShowPasswordForm(false);
                      resetPasswordForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary">
                    Update Password
                  </Button>
                </ButtonGroup>
              </Form>
            )}
          </Section>

          <Section>
            <SectionTitle>
              <Bell size={20} />
              Appearance
            </SectionTitle>
            <ThemeSetting />
          </Section>
        </MainContent>

        <Sidebar>
          <InfoCard>
            <SectionTitle>
              <Calendar size={20} />
              Account Information
            </SectionTitle>
            <InfoItem>
              <Mail size={16} color={theme.colors.gray400} />
              <InfoLabel>Email:</InfoLabel>
              <InfoValue>{user?.email}</InfoValue>
            </InfoItem>
            <InfoItem>
              <User size={16} color={theme.colors.gray400} />
              <InfoLabel>Username:</InfoLabel>
              <InfoValue>{user?.username}</InfoValue>
            </InfoItem>
            <InfoItem>
              <Calendar size={16} color={theme.colors.gray400} />
              <InfoLabel>Joined:</InfoLabel>
              <InfoValue>{formatDate(user?.created_at)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <Shield size={16} color={theme.colors.gray400} />
              <InfoLabel>Status:</InfoLabel>
              <InfoValue>Active</InfoValue>
            </InfoItem>
          </InfoCard>

          <DangerZone>
            <DangerTitle>
              <Trash2 size={20} />
              Danger Zone
            </DangerTitle>
            <DangerDescription>
              Once you delete your account, there is no going back. Please be certain.
            </DangerDescription>
            <Button 
              variant="danger" 
              onClick={handleDeleteAccount}
            >
              <Trash2 size={16} />
              Delete Account
            </Button>
          </DangerZone>
        </Sidebar>
      </ContentGrid>
    </ProfileContainer>
  );
}

export default ProfilePage;
